import React, { useEffect, useMemo } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { trimStart } from 'lodash-es'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Avatar, Box, Button, ComboboxItem, Input, Radio } from '@node-space/ui-components'
import { RequestError } from 'components/Errors/RequestError'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useRoutingNumberValidation } from 'hooks/queries/Beneficiaries/useRoutingNumberValidation'
import { useBanksQuery } from 'hooks/queries/useBanks'
import { useCountriesQuery } from 'hooks/queries/useCountriesQuery'
import { useIbanValidation } from 'hooks/queries/useIbanValidation'
import { useScanValidation } from 'hooks/queries/useScanValidation'
import { useSouthAfricanBanks } from 'hooks/queries/useSouthAfricanBanks'
import { useAddBeneficiaryContext } from 'hooks/useAddBeneficiaryContext'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import {
  AddBeneficiaryBasicPayload,
  AddBeneficiaryBasicPayloadKeys,
  AddBeneficiaryFormPayload,
  BeneficiaryDetailsCurrency,
  BeneficiaryEntityType,
  BeneficiaryTransferDestination,
  BeneficiaryType,
  PaymentMethod,
  USDBeneficiaryOptions,
  USDPaymentMethodRequiringAddress,
} from 'types/beneficiaries'
import { CountryCodes, Currencies } from 'types/types'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import {
  getFieldsToValidateForCurrency,
  getIsGbpAccountDetialsValid,
  resetCurrencySpecificFields,
} from 'utils/beneficiaries'
import track from 'utils/tracker'
import { titleCase } from 'utils/utils'
import { IBAN_VALIDATION_ERRORS } from '../../constants'
import { BeneficiaryAddressFields } from './BeneficiaryAddress/BeneficiaryAddress'
import { BusinessBeneficiaryFields } from './BeneficiaryEntityType/Business'
import { IndividualBeneficiaryFields } from './BeneficiaryEntityType/Individual'
import { BeneficaryTypeCheckbox } from './BeneficiaryTypeCheckbox'
import { GbpFormFields } from './Currencies/GbpFormFields'
import { IbanFormFields } from './Currencies/IbanFormFields'
import { NgnFormFields } from './Currencies/NgnFormFields'
import { UsdFormFields } from './Currencies/UsdFormFields'
import { ZarFormFields } from './Currencies/ZarFormFields'
import { addBeneficiaryDetailsSchema } from './schemas/addBeneficiaryDetailsSchema'

type AddBeneficiaryFormProps = {
  transferDestination: BeneficiaryTransferDestination
  isSubmitting: boolean
  onBack: () => void
  onContinue: (bankDetails: AddBeneficiaryBasicPayload) => void
}

export const AddBeneficiaryForm = ({
  transferDestination,
  isSubmitting,
  onBack,
  onContinue,
}: AddBeneficiaryFormProps) => {
  const { t } = useTranslation()
  const { accountCapabilities } = useAccountsContext()
  const { isCubixBeneficiaryCapabilityEnabled, isAedBeneficiaryEnabled } = accountCapabilities || {}
  const {
    beneficiaryFormState,
    beneficiaryFormErrors,
    setBeneficiaryFormErrors,
    setBeneficiaryFormState,
    setBeneficiaryIbanValidationState,
  } = useAddBeneficiaryContext()
  const {
    data: countries,
    isPending: isCountriesLoading,
    isError: isCountriesLoadingError,
  } = useCountriesQuery(true)

  const { requestError, setRequestError, resetRequestError } = useMappedErrorMessage()

  const formSchema = addBeneficiaryDetailsSchema(t)
  const {
    control,
    formState,
    register,
    setValue,
    resetField,
    trigger,
    clearErrors,
    watch,
    setError,
  } = useForm<AddBeneficiaryFormPayload>({
    resolver: yupResolver(formSchema),
  })

  const formValues = watch()

  const [
    alias,
    firstName,
    lastName,
    dateOfBirth,
    businessName,
    countryCode,
    currencyCode,
    beneficiaryEntityType,
    beneficiaryType,
    beneficiaryPaymentReference,
    accountType,
    beneficiaryPaymentMethod,
    // International beneficiary address
    addressLine1,
    addressLine2,
    city,
    postCode,
    region,
    // EUR
    iban,
    bic,
    // GBP
    accountNumber,
    sortCode,
    // NGN
    ngnAccountNumber,
    bankCode,
    // USD
    usdAccountNumber,
    usdAccountNumberConfirm,
    abaRoutingNumber,
    cubixAccountNumberDigits,
    cbitBankCode,
    // ZAR
    zarAccountNumber,
    zarAccountType,
    zarRoutingNumberBranchCode,
  ] = useWatch({
    control,
    name: [
      'alias',
      'firstName',
      'lastName',
      'dateOfBirth',
      'businessName',
      'countryCode',
      'currencyCode',
      'beneficiaryEntityType',
      'beneficiaryType',
      'beneficiaryPaymentReference',
      'accountType',
      'beneficiaryPaymentMethod',
      // International beneficiary address
      'addressLine1',
      'addressLine2',
      'city',
      'postCode',
      'region',
      // EUR
      'iban',
      'bic',
      // GBP
      'accountNumber',
      'sortCode',
      // NGN
      'ngnAccountNumber',
      'bankCode',
      // USD
      'usdAccountNumber',
      'usdAccountNumberConfirm',
      'abaRoutingNumber',
      'cubixAccountNumberDigits',
      'cbitBankCode',
      // ZAR
      'zarAccountNumber',
      'zarAccountType',
      'zarRoutingNumberBranchCode',
    ],
    defaultValue: {
      alias: '',
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      businessName: '',
      countryCode: '',
      currencyCode: '',
      beneficiaryEntityType: BeneficiaryEntityType.BUSINESS,
      beneficiaryType: BeneficiaryType.THIRD_PARTY,
      beneficiaryPaymentReference: '',
      accountType: '',
      beneficiaryPaymentMethod:
        beneficiaryFormState?.beneficiaryPaymentMethod ??
        (USDBeneficiaryOptions.ACH as PaymentMethod),
      // Bank details
      bankName: '',
      bankAddressLine1: '',
      bankAddressLine2: '',
      bankCity: '',
      bankPostCode: '',
      bankRegion: '',
      bankCountry: '',
      // International beneficiary address
      addressLine1: '',
      addressLine2: '',
      city: '',
      postCode: '',
      region: '',
      // EUR
      iban: '',
      bic: '',
      // GBP
      accountNumber: '',
      sortCode: '',
      // NGN
      ngnAccountNumber: '',
      bankCode: '',
      // USD
      usdAccountNumber: '',
      usdAccountNumberConfirm: '',
      abaRoutingNumber: '',
      cubixAccountNumberDigits: '',
      cbitBankCode: '',
      // ZAR
      zarAccountNumber: '',
      zarAccountType: '',
      zarRoutingNumberBranchCode: '',
    },
  })

  const entityOptions = [
    {
      value: BeneficiaryEntityType.BUSINESS,
      label: t('manageBeneficiaries.business'),
    },
    {
      value: BeneficiaryEntityType.INDIVIDUAL,
      label: t('manageBeneficiaries.individual'),
    },
  ]

  const isFirstPartyBeneficiaryEnabled = beneficiaryEntityType === BeneficiaryEntityType.BUSINESS
  const isLocalBeneficiary = transferDestination === BeneficiaryTransferDestination?.LOCAL
  const isInternationalBeneficiary =
    transferDestination === BeneficiaryTransferDestination?.INTERNATIONAL
  const isFirstPartyBeneficiary = beneficiaryType === BeneficiaryType.FIRST_PARTY
  const isFirstPartyBeneficiaryDisabled = currencyCode === BeneficiaryDetailsCurrency.ZAR

  const availableCurrencies = useMemo(() => {
    const beneficiaryAccountCurrencies: BeneficiaryDetailsCurrency[] = [
      BeneficiaryDetailsCurrency.EUR,
      BeneficiaryDetailsCurrency.GBP,
      BeneficiaryDetailsCurrency.USD,
    ]

    if (
      isInternationalBeneficiary &&
      isAedBeneficiaryEnabled &&
      !beneficiaryAccountCurrencies.includes(BeneficiaryDetailsCurrency.AED)
    ) {
      beneficiaryAccountCurrencies.push(BeneficiaryDetailsCurrency.AED)
    }
    // Temporarily remove ZAR from local beneficiary creation

    // if (
    //   isLocalBeneficiary &&
    //   !beneficiaryAccountCurrencies.includes(BeneficiaryDetailsCurrency.ZAR)
    // ) {
    //   beneficiaryAccountCurrencies.push(BeneficiaryDetailsCurrency.ZAR)
    // }

    return beneficiaryAccountCurrencies.map(currency => ({
      LeftElement: <Avatar name={currency.toLowerCase()} size="small" />,
      leftText: currency,
      value: currency,
    }))
  }, [
    transferDestination,
    beneficiaryFormState?.beneficiaryEntityType,
    isCubixBeneficiaryCapabilityEnabled,
    isAedBeneficiaryEnabled,
  ])

  const { bankList, isBankListError, isFetchingBankList } = useBanksQuery(
    CountryCodes.NG,
    Currencies.NGN,
    setRequestError,
    currencyCode === Currencies.NGN
  )

  const {
    data: ibanBankDetails,
    isFetching: isValidatingIban,
    isError: isIbanValidationError,
    refetch: validateIban,
  } = useIbanValidation(iban, 'IBAN', setRequestError, false)

  const {
    data: usBankDetails,
    isFetching: isValidatingRoutingNumber,
    isError: isValidatingRoutingNumberError,
    refetch: validateRoutingNumber,
  } = useRoutingNumberValidation(abaRoutingNumber, setRequestError, false)

  const {
    data: scanBankDetails,
    isFetching: isValidatingScan,
    refetch: validateScan,
  } = useScanValidation(sortCode, accountNumber, setRequestError, false)

  const {
    data: southAfricanBanks,
    isFetching: isFetchingSouthAfricanBanks,
    isError: isSouthAfricanBanksError,
  } = useSouthAfricanBanks(setRequestError, currencyCode === Currencies.ZAR)

  const ibanSupportedPaymentMethods = ibanBankDetails?.supportedPaymentMethods
  const scanSupportedPaymentMethods = scanBankDetails?.supportedPaymentMethods

  const isValidIban = useMemo(() => {
    switch (currencyCode) {
      case BeneficiaryDetailsCurrency.AED:
        return ibanBankDetails?.bankAccount?.bankCode && !ibanBankDetails?.validationFailed?.length
      case BeneficiaryDetailsCurrency.EUR:
        return (
          ibanSupportedPaymentMethods?.includes('SEPA_CT') ||
          ibanSupportedPaymentMethods?.includes('SEPA_INST')
        )
      default:
        return false
    }
  }, [ibanBankDetails, currencyCode])

  const isFPSandChapsSupported =
    scanSupportedPaymentMethods?.includes('CHAPS') ||
    scanSupportedPaymentMethods?.includes('BACS') ||
    scanSupportedPaymentMethods?.includes('FASTER_PAYMENT') ||
    scanSupportedPaymentMethods?.includes('CCC_PAYMENTS')

  const isInvalidRoutingNumber =
    !!usBankDetails?.validationFailed?.length || isValidatingRoutingNumberError

  useEffect(() => {
    if (ibanBankDetails?.bankAccount?.bankCode) {
      handleChange('bic', ibanBankDetails?.bankAccount?.bankCode)
    } else {
      handleChange('bic', '')
    }
  }, [ibanBankDetails?.bankAccount?.bankCode])

  useEffect(() => {
    if (!!abaRoutingNumber && !!usBankDetails && !!usBankDetails?.validationFailed?.length) {
      setBeneficiaryFormErrors({
        ...beneficiaryFormErrors,
        abaRoutingNumber: { message: t('abaRoutingNumberError') },
      })
    }
  }, [usBankDetails, abaRoutingNumber])

  useEffect(() => {
    setValue('beneficiaryEntityType', BeneficiaryEntityType.BUSINESS)
    setCachedValues()
    track.Amp.track(AmplitudeEvent.ADD_BENEFICIARY_INITIATE, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.VIEW,
      transferDestination: transferDestination?.toLowerCase(),
      entityType: BeneficiaryEntityType.BUSINESS.toLowerCase(),
    })
  }, [])

  useEffect(() => {
    if (isIbanValidationError) {
      setBeneficiaryFormErrors({
        ...beneficiaryFormErrors,
        iban: { message: t('ibanValidationError') },
      })

      return
    }

    if (ibanBankDetails?.validationFailed?.length >= 1) {
      const countryDoesNotSupportIban = ibanBankDetails.validationFailed.includes(
        IBAN_VALIDATION_ERRORS.COUNTRY_DOES_NOT_SUPPORT_IBAN
      )

      if (!countryDoesNotSupportIban) {
        setBeneficiaryFormErrors({
          ...beneficiaryFormErrors,
          iban: { message: t('invalidIbanErrorMessage') },
        })
      }
    } else if (ibanBankDetails && !isValidIban) {
      setBeneficiaryFormErrors({
        ...beneficiaryFormErrors,
        iban: { message: t('reachabilityCheckErrorMessage') },
      })
      clearErrors()
    }
  }, [ibanBankDetails, isIbanValidationError])

  useEffect(() => {
    if (scanBankDetails && !isFPSandChapsSupported) {
      setBeneficiaryFormErrors({
        ...beneficiaryFormErrors,
        accountNumber: { message: t('reachabilityCheckErrorMessage') },
      })
    }
  }, [scanBankDetails])

  useEffect(() => {
    if (beneficiaryFormErrors) {
      Object.keys(beneficiaryFormErrors).forEach((field: AddBeneficiaryBasicPayloadKeys) => {
        setError(field, beneficiaryFormErrors[field])
      })
    } else {
      clearErrors()
    }
  }, [setError, clearErrors, beneficiaryFormErrors])

  useEffect(() => {
    if (currencyCode === Currencies.ZAR) {
      setValue('beneficiaryType', BeneficiaryType.FIRST_PARTY)
    } else {
      setValue('beneficiaryType', BeneficiaryType.THIRD_PARTY)
    }

    resetBankDetailsOnCurrencyChange()
  }, [currencyCode])

  const resetBankDetailsOnCurrencyChange = () => {
    if (
      !!currencyCode &&
      !!beneficiaryFormState?.currencyCode &&
      beneficiaryFormState?.currencyCode !== currencyCode
    ) {
      setValue('intlIban', '')
      setValue('bicSwiftCode', '')
      setValue('bankAddressLine1', '')
      setValue('bankAddressLine2', '')
      setValue('bankCity', '')
      setValue('bankCountry', '')
      setValue('bankCountryName', '')
      setValue('bankName', '')
      setValue('bankPostCode', '')
      setValue('bankRegion', '')
      setBeneficiaryIbanValidationState(null)
      setBeneficiaryFormState({
        ...beneficiaryFormState,
        intlIban: '',
        bicSwiftCode: '',
        bankAddressLine1: '',
        bankAddressLine2: '',
        bankCity: '',
        bankCountry: '',
        bankCountryName: '',
        bankName: '',
        bankPostCode: '',
        bankCode: '',
        bankRegion: '',
      })
    }
  }

  const isInValidEurDetails =
    ibanSupportedPaymentMethods &&
    !isValidIban &&
    isLocalBeneficiary &&
    currencyCode === Currencies.EUR

  const isInvalidForm =
    isBankListError ||
    isSouthAfricanBanksError ||
    isCountriesLoadingError ||
    isInValidEurDetails ||
    isInvalidRoutingNumber ||
    isIbanValidationError

  useEffect(() => {
    if (
      !isBankListError &&
      !isSouthAfricanBanksError &&
      !isCountriesLoadingError &&
      !isInValidEurDetails
    ) {
      resetRequestError()
    }
  }, [isBankListError, isSouthAfricanBanksError, isCountriesLoadingError, isInValidEurDetails])

  const setCachedValues = () => {
    Object.keys(beneficiaryFormState).forEach((field: keyof AddBeneficiaryBasicPayload) => {
      if (beneficiaryFormState[field]) {
        setValue(field, beneficiaryFormState[field])
      }
    })
  }

  const handleContinue = async () => {
    resetRequestError()
    const validateFields = getFieldsToValidateForCurrency(
      currencyCode,
      beneficiaryEntityType,
      transferDestination,
      formValues?.beneficiaryPaymentReference,
      beneficiaryPaymentMethod as PaymentMethod
    )
    if (formState?.errors?.iban?.message === t('invalidIbanErrorMessage')) {
      clearErrors(validateFields)
      await trigger(validateFields)
    } else {
      clearErrors()
    }
    resetCurrencySpecificFields(currencyCode, resetField, beneficiaryPaymentMethod as PaymentMethod)

    const verifyFields = await trigger(validateFields)
    if (verifyFields) {
      if (
        currencyCode === Currencies.GBP &&
        transferDestination === BeneficiaryTransferDestination.LOCAL
      ) {
        const scanValidationResponse = await validateScan()
        const isValid = getIsGbpAccountDetialsValid(scanValidationResponse?.data)
        const isSortCodeValid = isValid?.sortCode
        const isAccountNumberValid = isValid?.accountNumber

        if (!isSortCodeValid || !isAccountNumberValid) {
          !isSortCodeValid && setError('sortCode', { message: t('invalidSortCodeErrorMessage') })
          !isAccountNumberValid &&
            setError('accountNumber', { message: t('invalidAccountNumberErrorMessage') })
        } else {
          onContinue(formValues)
        }
      } else {
        onContinue(formValues)
      }
    }
  }

  const handleInternationalIbanOnBlur = async () => {
    clearErrors()
    const isValid = await trigger('iban')

    if (!!iban && isValid) {
      validateIban()
    }
  }

  const handleOnRoutingNumberBlur = () => {
    clearErrors()
    !!abaRoutingNumber && validateRoutingNumber()
  }

  const setTab = (beneficiaryEntityTypeTab: BeneficiaryEntityType) => {
    setValue('beneficiaryEntityType', beneficiaryEntityTypeTab)

    if (beneficiaryEntityTypeTab === BeneficiaryEntityType.INDIVIDUAL) {
      setValue('beneficiaryType', BeneficiaryType.THIRD_PARTY)
    }

    track.Amp.track(AmplitudeEvent.ADD_BENEFICIARY_INITIATE, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.CLICK,
      transferDestination: transferDestination?.toLowerCase(),
      entityType: beneficiaryEntityTypeTab?.toLowerCase(),
    })
  }

  const beneficiaryFormLoading = useMemo(() => {
    return isSubmitting || isValidatingIban || isValidatingScan || isValidatingRoutingNumber
  }, [isSubmitting, isValidatingIban, isValidatingScan])

  const getBankOptions = (): ComboboxItem[] => {
    return bankList?.banks?.map(bank => {
      return {
        leftText: titleCase(bank?.bankName),
        value: bank?.bankIdentifier,
      }
    })
  }
  const southAfricanBankOptions: ComboboxItem[] = useMemo(() => {
    return southAfricanBanks?.map(bank => ({
      leftText: titleCase(bank?.name),
      value: bank?.id?.toString(),
    }))
  }, [southAfricanBanks])

  const handleChange = (field: AddBeneficiaryBasicPayloadKeys, value: string) => {
    setValue(field, trimStart(value))
  }

  const renderAccountTypeFields = () => {
    switch (formValues?.beneficiaryEntityType) {
      case BeneficiaryEntityType.BUSINESS:
        return (
          <BusinessBeneficiaryFields
            availableCurrencies={availableCurrencies}
            alias={alias}
            businessName={businessName}
            countries={countries?.map(country => ({
              leftText: country?.name,
              value: country?.code,
            }))}
            isCountriesLoading={isCountriesLoading}
            countryCode={countryCode}
            currencyCode={currencyCode}
            errors={formState?.errors}
            register={register}
            setValue={setValue}
            renderCurrencySpecificFields={isLocalBeneficiary && renderCurrencySpecificFields}
          />
        )
      case BeneficiaryEntityType.INDIVIDUAL:
        return (
          <IndividualBeneficiaryFields
            availableCurrencies={availableCurrencies}
            alias={alias}
            countries={countries?.map(country => ({
              leftText: country?.name,
              value: country?.code,
            }))}
            isCountriesLoading={isCountriesLoading}
            countryCode={countryCode}
            currencyCode={currencyCode}
            errors={formState?.errors}
            firstName={firstName}
            lastName={lastName}
            dateOfBirth={dateOfBirth}
            register={register}
            setValue={setValue}
            renderCurrencySpecificFields={isLocalBeneficiary && renderCurrencySpecificFields}
            isLocalBeneficiary={isLocalBeneficiary}
          />
        )
      default:
        return null
    }
  }

  const isUsdCubix =
    currencyCode === BeneficiaryDetailsCurrency.USD &&
    beneficiaryPaymentMethod === USDBeneficiaryOptions.CUBIX
  const usdPaymentMethodsRequiresAddress = [
    USDBeneficiaryOptions.ACH,
    USDBeneficiaryOptions.ACH_SAME_DAY,
    USDBeneficiaryOptions.FEDWIRE,
    USDBeneficiaryOptions.RTP,
    USDBeneficiaryOptions.FEDNOW,
    USDBeneficiaryOptions.CRNOW,
  ]

  const isUsdPaymentMethodRequiresAddress =
    currencyCode === BeneficiaryDetailsCurrency.USD &&
    usdPaymentMethodsRequiresAddress.includes(
      beneficiaryPaymentMethod as USDPaymentMethodRequiringAddress
    )

  const isIbanCurrency =
    currencyCode === BeneficiaryDetailsCurrency.EUR ||
    currencyCode === BeneficiaryDetailsCurrency.AED

  const renderCurrencySpecificFields = () => {
    return (
      <Box>
        {isIbanCurrency && (
          <IbanFormFields
            bic={bic}
            errors={formState?.errors}
            iban={iban}
            handleChange={handleChange}
            register={register}
            onIbanInputBlur={handleInternationalIbanOnBlur}
            isValidatingIban={isValidatingIban}
            ibanValidationData={ibanBankDetails}
            isSepaSupported={isValidIban}
          />
        )}
        {currencyCode === BeneficiaryDetailsCurrency.GBP && (
          <GbpFormFields
            accountNumber={accountNumber}
            errors={formState?.errors}
            sortCode={sortCode}
            handleChange={handleChange}
            register={register}
            isValidatingScan={isValidatingScan}
            scanValidationData={scanBankDetails}
          />
        )}
        {currencyCode === BeneficiaryDetailsCurrency.NGN && (
          <NgnFormFields
            bankCode={bankCode}
            banks={getBankOptions()}
            errors={formState?.errors}
            fetchingBankList={isFetchingBankList}
            ngnAccountNumber={ngnAccountNumber}
            handleChange={handleChange}
            register={register}
          />
        )}
        {currencyCode === BeneficiaryDetailsCurrency.USD && (
          <UsdFormFields
            abaRoutingNumber={abaRoutingNumber}
            errors={formState?.errors}
            cubixAccountNumberDigits={cubixAccountNumberDigits}
            cbitBankCode={cbitBankCode}
            usdAccountNumber={usdAccountNumber}
            usdAccountNumberConfirm={usdAccountNumberConfirm}
            addressLine1={addressLine1}
            addressLine2={addressLine2}
            city={city}
            postCode={postCode}
            region={region}
            accountType={accountType}
            beneficiaryPaymentMethod={beneficiaryPaymentMethod as PaymentMethod}
            isValidatingRoutingNumber={isValidatingRoutingNumber}
            usBankDetails={usBankDetails}
            handleChange={handleChange}
            register={register}
            onRoutingNumberBlur={handleOnRoutingNumberBlur}
          />
        )}
        {currencyCode === BeneficiaryDetailsCurrency.ZAR && (
          <ZarFormFields
            routingNumberBranchCode={zarRoutingNumberBranchCode}
            accountType={zarAccountType}
            selectedBank={bankCode}
            bankOptions={southAfricanBankOptions}
            errors={formState?.errors}
            accountNumber={zarAccountNumber}
            isFetchingBanks={isFetchingSouthAfricanBanks}
            handleChange={handleChange}
            register={register}
            setValue={setValue}
          />
        )}

        {!isUsdCubix && (
          <Box flexCol gapY={16}>
            <h2 className="text-foreground-prominent text-h2">
              {t('formFields.beneficiaryAddress')}
            </h2>
            <BeneficiaryAddressFields
              isOptional={!isUsdPaymentMethodRequiresAddress}
              addressLine1={addressLine1}
              addressLine2={addressLine2}
              city={city}
              errors={formState?.errors}
              postCode={postCode}
              region={region}
              handleChange={handleChange}
              register={register}
            />
          </Box>
        )}
      </Box>
    )
  }

  const renderSwiftBeneficiaryDetails = () => {
    return (
      <BeneficiaryAddressFields
        addressLine1={addressLine1}
        addressLine2={addressLine2}
        city={city}
        errors={formState?.errors}
        postCode={postCode}
        region={region}
        handleChange={handleChange}
        register={register}
      />
    )
  }

  const renderTab = () => {
    return (
      <>
        {requestError?.show && (
          <Box paddingB={20}>
            <RequestError errorCode={requestError?.errorCode} />
          </Box>
        )}

        {renderAccountTypeFields()}
        <Box paddingB={16}>
          <Input
            {...register('beneficiaryPaymentReference')}
            label={`${t('beneficiaryPaymentReference')} (${t('optional')})`}
            inputMode="text"
            name="beneficiaryPaymentReference"
            test-id="input-beneficiaryPaymentReference"
            errorText={formState?.errors?.beneficiaryPaymentReference?.message}
            error={!!formState?.errors?.beneficiaryPaymentReference}
            value={beneficiaryPaymentReference}
            onChange={event => handleChange('beneficiaryPaymentReference', event?.target?.value)}
            placeholder={t('inputPlaceholders.enterReferencePlaceholder')}
          />
        </Box>
        {isInternationalBeneficiary && renderSwiftBeneficiaryDetails()}
        {isFirstPartyBeneficiaryEnabled && (
          <BeneficaryTypeCheckbox
            disabled={isFirstPartyBeneficiaryDisabled}
            isFirstPartyBeneficiary={isFirstPartyBeneficiary}
            setValue={setValue}
          />
        )}
      </>
    )
  }

  return (
    <Box data-testid="add-beneficiary-form" flex flexCol width="full">
      <Radio
        {...register('beneficiaryEntityType')}
        name="beneficiaryEntityType"
        horizontal
        key={beneficiaryFormState?.beneficiaryEntityType}
        testId="beneficiary-entity-type"
        options={entityOptions}
        value={beneficiaryEntityType}
        error={formState?.errors?.beneficiaryEntityType?.message}
        onChange={value => setTab(value as BeneficiaryEntityType)}
      />

      <Box paddingT={16}>{renderTab()}</Box>
      <Box flex justify="between" paddingT={24}>
        <Button
          onClick={() => {
            resetRequestError()
            clearErrors()
            onBack()
          }}
          variant="secondary"
        >
          {t('back')}
        </Button>
        <Button
          testId={isLocalBeneficiary ? 'add-beneficiary' : 'continue'}
          onClick={handleContinue}
          loading={beneficiaryFormLoading}
          disabled={isInvalidForm}
        >
          {isLocalBeneficiary ? t('manageBeneficiaries.addBeneficiaryCta') : t('continue')}
        </Button>
      </Box>
    </Box>
  )
}
